import ReactGA4 from 'react-ga4';

import config from '../../config';
import { formatPrice, getPriceCent, getPriceFromCent } from '../../util/price';

import { getProduct, getProductPrice } from '../Product/ProductActions';
import { getShippingFeeAmount, getShippingFeeByAddress } from '../ShippingFee/ShippingFeeActions';
import { getTaxByCountry } from '../Tax/TaxActions';
import { checkVoucherConditionsFromStore, getVoucherDiscountValue } from '../Voucher/VoucherActions';
import { getTranslation } from '../Intl/IntlActions';
import { slugify } from '../../util/text';


export const SET_ITEM = 'SET_ITEM';
export const REMOVE_ITEM = 'REMOVE_ITEM';
export const SET_SHIPPING_ADDRESS = 'SET_SHIPPING_ADDRESS';
export const SET_SHIPPING_AMOUNT = 'SET_SHIPPING_AMOUNT';
export const SET_CART_SUBSCRIPTION = 'SET_CART_SUBSCRIPTION';
export const REMOVE_CART_SUBSCRIPTION = 'REMOVE_CART_SUBSCRIPTION';
export const CLEAR_CART = 'CLEAR_CART';


// Getters
export function getCartItems(store) {
    return store.cart.items;
}

export function getCartItemByProduct(store, product) {
    return store.cart.items.find(item => item.productId === product._id);
}

export function getCartItemsCount(store) {
    return store.cart.items.reduce((total, item) => total + (item.quantity || 0) + (item.freeCount || 0), 0);
}

export function getCartItemsTotals(store) {
    return getPriceFromCent(store.cart.items.reduce((total, item) => total + getPriceCent(getProductPrice(getProduct(store, item.productId), item.quantity)), 0));
}

export function getCartShipping(store) {
    return store.cart.shipping;
}

export function getCartVoucher(store) {
    return store.cart.voucher;
}

export function getCartItemFromProduct(product, quantity, pack, locale) {
    return {
        productId: product._id,
        name: getTranslation(product, 'name', locale),
        sku: getTranslation(product, 'sku', locale),
        quantity: parseInt(quantity, 10),
        freeCount: parseInt(pack ? pack.freeCount : 0, 10),
        pack,
        unitPriceHT: pack ? pack.unitPriceHT : product.priceHT,
        priceHT: getProductPrice(product, quantity),
        weight: product.weight || 0,
    };
}

export function isCartValid(store) {
    return getCartItemsCount(store) > 0 && getCartShipping(store) && !!getShippingFeeByAddress(store, (getCartShipping(store) || {}).address);
}

export function getCartSubscription(store) {
    return store.cart.subscription;
}

export function getCartShippingValue(store) {
    const shipping = getCartShipping(store);
    const shippingFeeAmount = getShippingFeeAmount(store, shipping && shipping.address);
    return shipping && shipping.address && shippingFeeAmount !== null ? shippingFeeAmount : null;
}

export function getCartVoucherValue(store) {
    const voucher = getCartVoucher(store);
    return checkVoucherConditionsFromStore(store, voucher) ? getVoucherDiscountValue(voucher, getCartItemsTotals(store), getCartShippingValue(store)) : 0;
}

export function getCartSubTotalValue(store) {
    return getPriceFromCent(
        getPriceCent(getCartItemsTotals(store))
        + getPriceCent(getCartShippingValue(store) || 0)
        - getPriceCent(getCartVoucherValue(store)),
    );
}

export function getCartTaxValue(store) {
    const shipping = getCartShipping(store);
    const tax = getTaxByCountry(store, shipping && shipping.address && shipping.address.countryCode);
    return getPriceFromCent((tax ? tax.rate : 0) * getPriceCent(getCartSubTotalValue(store)));
}

export function getCartTotalValue(store) {
    return getPriceFromCent(
        getPriceCent(getCartSubTotalValue(store))
        + getPriceCent(getCartTaxValue(store)),
    );
}

export function getCartTotals(store) {
    return {
        items: getCartItemsTotals(store),
        shippingFee: getCartShippingValue(store),
        voucher: getCartVoucherValue(store),
        subtotalHT: getCartSubTotalValue(store),
        tax: getCartTaxValue(store),
        totalTTC: getCartTotalValue(store),
    };
}

export function getTotalWeight(items, products = null) {
    return getPriceFromCent((items || []).reduce((total, item) => {
        const product = false && products && products.length ? products.find(product => product._id === item.productId) : null;
        return total + getPriceCent(item.quantity * (product && !item.forceUseItemData ? product.weight : item.weight));
    }, 0));
}

export function getTotalQuantity(items) {
    return getPriceFromCent((items || []).reduce((quantity, item) => quantity + getPriceCent((item && item.quantity * ((item.items || []).length ? item.items.reduce((packItemTotal, packItem) => packItemTotal + packItem.quantity, 0) : 1)) || 0), 0));
}

export function getSubtotal(items, products = null, includingTax = false) {
    return getPriceFromCent(getPriceCent((items || []).reduce((total, item) => {
        const priceKey = includingTax ? 'priceTTC' : 'priceHT';
        // const product = false && products && products.length ? products.find(product => product._id === item.productId) : null;
        // return total + (item.quantity * (product && !item.forceUseItemData ? product[priceKey] : item[priceKey]) || 0);
        return total + item[priceKey];
    }, 0)));
}

export function trackAnalytics(order) {
    if(typeof window !== 'undefined') {
        if(config.google.analytics.trackingCodeGA4) {
            ReactGA4.event('purchase', {
                currency: order.currency,
                value: parseFloat(order.totals.totalTTC),
                coupon: (order.vouchers.find(voucher => voucher) || {}).code || '',
                items: formatAnalyticsItems(order.items || []),
                transaction_id: order.code || 'NC',
                shipping: order.totals.shippingFee,
                tax: order.totals.tax,
                discount: order.totals.voucher,
            });
        } else {
            console.error('GA not configured');
        }

        if(window.dataLayer) {
            window.dataLayer.push({ order_id: order._id });
            window.dataLayer.push({ order_code: order.code });
            window.dataLayer.push({ order_amount: order.totals.totalTTC });
        } else {
            console.error('DataLayer does not exist');
        }

        if(window.fbq) {
            window.fbq('track', 'Purchase', {
                currency: order.currency,
                value: order.totals.totalTTC,
                contents: order.items.map(item => {
                    return {
                        id: item.sku || item.productId,
                        name: item.name,
                        quantity: item.quantity,
                    };
                }),
            });
        } else {
            console.error('FbPixel not configured');
        }
    }
}

export function formatAnalyticsItems(items, listId = '') {
    return (items || []).map(item => ({
        id: item.sku || item.productId || slugify(item.name),
        item_id: item.sku || item.productId || slugify(item.name),
        item_name: item.sku || item.name,
        price: item.priceHT,
        quantity: parseInt(item.quantity, 10),
        item_brand: config.application.name,
        discount: formatPrice(parseFloat(item.unitPriceHT * item.quantity) - parseFloat(item.priceHT)),
        item_list_id: listId,
    }));
}

// Actions
export function setCartItem(item) {
    return {
        type: SET_ITEM,
        item,
    };
}

export function removeItem(item) {
    return {
        type: REMOVE_ITEM,
        item,
    };
}

export function clearCart() {
    return {
        type: CLEAR_CART,
    };
}

export function setShippingAddress(address = {}) {
    return {
        type: SET_SHIPPING_ADDRESS,
        address,
    };
}

export function setShippingAmount(amount = null) {
    return {
        type: SET_SHIPPING_AMOUNT,
        amount,
    };
}

export function setCartSubscriptionPeriod(periodUnit, periodValue) {
    return {
        type: SET_CART_SUBSCRIPTION,
        subscription: {
            periodIntervalUnit: periodUnit,
            periodIntervalValue: periodValue,
        },
    };
}

export function removeCartSubscription() {
    return {
        type: REMOVE_CART_SUBSCRIPTION,
    };
}
